/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { DealerType } from '@schemas'
import { customerRoleLabel } from '@src/configs/customerRole.config'
import { mono, boFont, boShared } from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	dealers: DealerType[]
	selectedCustomerId: string
	onSelect: (dealer: DealerType) => void
}

/** 기본 규칙 한 줄 — `75%` · `−10,000` · `= 300` · `정가` */
export const ruleLabel = (
	ruleType: DealerType['defaultRuleType'],
	rate: number | null,
	amountKrw: number | null,
	amountUsd: number | null,
	currency: DealerType['currency'],
): string => {
	if (ruleType === null) return '정가'
	if (ruleType === 'rate') {
		return rate === null ? '?' : `${Math.round(rate * 10000) / 100}%`
	}
	const amount = currency === 'KRW' ? amountKrw : amountUsd
	const text =
		amount === null
			? '?'
			: currency === 'KRW'
			? Math.round(amount).toLocaleString('ko-KR')
			: amount.toFixed(2)
	return ruleType === 'minus' ? `−${text}` : `= ${text}`
}

/**
 * 납품가 관리 좌측 — 역할(DEALER·DISTRIBUTOR)이 유효한 거래처 목록 (DISPATCH_PLAN §4.3).
 * 행 = 업체명(없으면 고객 이름) · 이메일 · 역할 칩 · 통화 · 기본 규칙.
 */
export const DealerList = (props: PropsType): JSX.Element => {
	return (
		<View css={style}>
			{props.dealers.length === 0 ? (
				<p className="boEmpty">
					거래처가 없습니다 — 고객 관리에서 고객 유형을 Dealer 또는 Distributor 로
					지정하면 여기 섭니다.
				</p>
			) : (
				<ul className="dealerList">
					{props.dealers.map(aDealer => {
						const isSelected = aDealer.customerId === props.selectedCustomerId
						return (
							<li key={aDealer.customerId}>
								<button
									type="button"
									className={`dealerRow ${isSelected ? 'on' : ''}`}
									onClick={() => props.onSelect(aDealer)}
								>
									<span className="dealerName">
										{aDealer.companyName !== '' ? aDealer.companyName : aDealer.name}
									</span>
									<span className="dealerEmail">{aDealer.email}</span>
									<span className="dealerMeta">
										{aDealer.roles.map(aRole => (
											<span key={aRole} className="boChip">
												{customerRoleLabel(aRole)}
											</span>
										))}
										<span className="dealerCurrency">{aDealer.currency}</span>
										<span className="dealerRule">
											{ruleLabel(
												aDealer.defaultRuleType,
												aDealer.defaultRate,
												aDealer.defaultAmountKrw,
												aDealer.defaultAmountUsd,
												aDealer.currency,
											)}
											{aDealer.ruleCount > 0 ? ` · 예외 ${aDealer.ruleCount}` : ''}
										</span>
										{!aDealer.hasProfile && <span className="dealerNew">미설정</span>}
									</span>
								</button>
							</li>
						)
					})}
				</ul>
			)}
		</View>
	)
}

const style = css({
	...boShared,
	width: '100%',

	'.dealerList': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},

	'.dealerRow': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 6,
		padding: '14px 12px',
		border: 0,
		borderBottom: `1px solid ${mono.line}`,
		background: 'transparent',
		textAlign: 'left',
		cursor: 'pointer',
		fontFamily: boFont.ui,
		borderLeft: '1px solid transparent',

		'&:hover': { background: mono.bg },
		'&.on': {
			background: 'rgba(22,24,27,.05)',
			borderLeftColor: mono.ink,
		},

		'.dealerName': { fontSize: 14, fontWeight: 500, color: mono.ink },
		'.dealerEmail': { fontSize: 12, color: mono.dim },
		'.dealerMeta': {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			flexWrap: 'wrap',
			fontSize: 12,
			color: mono.faint,
			marginTop: 2,
		},
		'.dealerCurrency': { letterSpacing: '.08em', fontWeight: 500 },
		'.dealerRule': { fontVariantNumeric: 'tabular-nums' },
		'.dealerNew': { color: mono.dim },
	},
})
